import React from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Eye, EyeOff } from 'lucide-react';

/**
 * Add / edit shipping company modal (Phase 8).
 *
 * Props:
 *  - open, onClose
 *  - mode: 'create' | 'edit'
 *  - company (edit mode only)
 *  - onSubmit(form) — parent handles the API call + toast + reload
 *
 * On edit:
 *   - api_key / api_secret start EMPTY (they're never sent to the browser
 *     as clear text). The user can either leave them blank (untouched — the
 *     backend preserves the current encrypted value) or type new ones to
 *     replace them.
 *   - The Eye icon next to each input toggles reveal-mode for the input the
 *     user is currently editing (does NOT reveal server values — that's a
 *     separate flow on the table).
 */
export default function ShippingCompanyDialog({ open, onClose, mode = 'create', company = null, onSubmit }) {
    const isEdit = mode === 'edit';
    const [form, setForm] = React.useState({ name: '', api_key: '', api_secret: '', is_active: true });
    const [showKey, setShowKey] = React.useState(false);
    const [showSecret, setShowSecret] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setSubmitting(false);
        setShowKey(false);
        setShowSecret(false);
        if (isEdit && company) {
            setForm({ name: company.name || '', api_key: '', api_secret: '', is_active: !!company.is_active });
        } else {
            setForm({ name: '', api_key: '', api_secret: '', is_active: true });
        }
    }, [open, isEdit, company]);

    const submit = async () => {
        setError(null);
        const name = form.name.trim();
        if (!name) { setError('Nom requis.'); return; }
        setSubmitting(true);
        try {
            // On create → send everything. On edit → only send the fields the
            // user filled in so the backend preserves the untouched credentials.
            const payload = isEdit
                ? {
                    name,
                    is_active: form.is_active,
                    ...(form.api_key.length ? { api_key: form.api_key } : {}),
                    ...(form.api_secret.length ? { api_secret: form.api_secret } : {}),
                }
                : {
                    name,
                    is_active: form.is_active,
                    api_key: form.api_key || null,
                    api_secret: form.api_secret || null,
                };
            await onSubmit(payload);
        } catch (e) {
            setError(e.message || 'Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="bg-white sm:max-w-[520px]" data-testid={isEdit ? 'shipping-edit-dialog' : 'shipping-create-dialog'}>
                <DialogHeader>
                    <DialogTitle className="text-[#1A1D2E]">
                        {isEdit ? 'Modifier le transporteur' : 'Ajouter un transporteur'}
                    </DialogTitle>
                    <DialogDescription className="text-[#6B7280]">
                        Les identifiants API sont chiffrés (Fernet) avant d'être stockés en base.
                        {isEdit && ' Laissez les champs vides pour conserver les valeurs actuelles.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <FormRow label="Nom">
                        <input
                            data-testid="shipping-form-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Ex: OLIVRAISON"
                            className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                        />
                    </FormRow>

                    <FormRow label={`API Key ${isEdit ? '(laisser vide pour ne pas modifier)' : '(optionnel)'}`}>
                        <div className="relative">
                            <input
                                data-testid="shipping-form-api-key"
                                type={showKey ? 'text' : 'password'}
                                value={form.api_key}
                                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                                placeholder={isEdit ? '••••••••' : ''}
                                className="h-10 px-3 pr-10 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                            />
                            <button
                                type="button"
                                data-testid="shipping-form-toggle-key"
                                onClick={() => setShowKey((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded hover:bg-[#F5F6FA] text-[#6B7280]"
                                aria-label={showKey ? 'Masquer' : 'Afficher'}
                            >
                                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </FormRow>

                    <FormRow label={`API Secret ${isEdit ? '(laisser vide pour ne pas modifier)' : '(optionnel)'}`}>
                        <div className="relative">
                            <input
                                data-testid="shipping-form-api-secret"
                                type={showSecret ? 'text' : 'password'}
                                value={form.api_secret}
                                onChange={(e) => setForm({ ...form, api_secret: e.target.value })}
                                placeholder={isEdit ? '••••••••' : ''}
                                className="h-10 px-3 pr-10 rounded-lg border border-[#E5E7EB] bg-white text-sm text-[#1A1D2E] w-full focus:outline-none focus:ring-2 focus:ring-[#4B3FD8]/25"
                            />
                            <button
                                type="button"
                                data-testid="shipping-form-toggle-secret"
                                onClick={() => setShowSecret((v) => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center rounded hover:bg-[#F5F6FA] text-[#6B7280]"
                                aria-label={showSecret ? 'Masquer' : 'Afficher'}
                            >
                                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </FormRow>

                    <FormRow label="Statut">
                        <div className="flex items-center gap-4">
                            <label className="inline-flex items-center gap-2 text-sm text-[#1A1D2E]">
                                <input type="radio" checked={form.is_active === true} onChange={() => setForm({ ...form, is_active: true })} data-testid="shipping-form-status-active" />
                                Actif
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-[#1A1D2E]">
                                <input type="radio" checked={form.is_active === false} onChange={() => setForm({ ...form, is_active: false })} data-testid="shipping-form-status-inactive" />
                                Inactif
                            </label>
                        </div>
                    </FormRow>

                    {error && (
                        <div data-testid="shipping-form-error" className="text-sm text-[#EF4444]">{error}</div>
                    )}
                </div>

                <DialogFooter>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-10 px-4 rounded-lg border border-[#E5E7EB] bg-white text-[#1A1D2E] text-sm hover:bg-[#F5F6FA]"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        data-testid="shipping-form-submit"
                        onClick={submit}
                        disabled={submitting}
                        className="h-10 px-4 rounded-lg bg-gradient-to-r from-[#4B3FD8] to-[#9B7FF4] text-white text-sm font-semibold disabled:opacity-40"
                    >
                        {submitting ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Ajouter')}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FormRow({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[12px] uppercase tracking-wider text-[#6B7280] font-semibold">{label}</span>
            {children}
        </label>
    );
}
